import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Check, X, Search } from 'lucide-react'

/**
 * Typeable dropdown with full keyboard control — the app's one picker for
 * "choose from a list that is too long to be a row of chips".
 *
 *   ↑ / ↓     move the highlight (wraps; skips nothing — the list is the filter's
 *             output, so what you see is what you can reach)
 *   Enter     take the highlighted option (multi: toggle it, list stays open)
 *   Esc       close, restoring the trigger's focus
 *   ⌫         on an empty query, remove the last chosen value (multi only)
 *
 * `multiple` switches between one value (`value` / onChange(value|'')) and a Set
 * (`values` / onChange(Set)). Options are
 * `{ value, label, hint?, count?, search?, group?, selectedLabel? }`, where `search` is
 * matched by the query but never rendered, `group` draws a sticky section header each time
 * it changes down the list (so the caller must sort by it — this renders groups, it does
 * not form them), and `selectedLabel` is what the closed trigger shows in place of `label`,
 * for the case where the header carries context the shut dropdown loses.
 *
 * The highlight is index-based over the FILTERED list and resets to 0 on every
 * keystroke, so typing then hitting Enter always takes the top match — the thing
 * the user was aiming at — rather than a stale row from the previous query.
 */
export default function SearchableSelect({
  options = [],
  value = '',
  values,
  multiple = false,
  onChange,
  placeholder = 'เลือก…',
  searchPlaceholder = 'พิมพ์เพื่อค้นหา…',
  emptyText = 'ไม่พบตัวเลือก',
  disabled = false,
  className = '',
  buttonClassName = '',
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(0)
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const sel = multiple ? (values instanceof Set ? values : new Set(values || [])) : null

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return options
    return options.filter((o) =>
      String(o.label || '').toLowerCase().includes(needle) ||
      String(o.value || '').toLowerCase().includes(needle) ||
      String(o.hint || '').toLowerCase().includes(needle) ||
      String(o.group || '').toLowerCase().includes(needle) ||
      // Optional extra haystack for fields worth SEARCHING but not worth showing —
      // e.g. a billing type's English name and receipt category, which would make the
      // hint line unreadable if they had to be visible to be findable.
      String(o.search || '').toLowerCase().includes(needle))
  }, [options, q])

  useEffect(() => { setHi(0) }, [q, open])

  // Close on an outside click / focus leaving the widget.
  useEffect(() => {
    if (!open) return
    function onDown(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => { if (open) inputRef.current?.focus() }, [open])

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    if (!open || !listRef.current) return
    const el = listRef.current.querySelector(`[data-idx="${hi}"]`)
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [hi, open])

  function commit(opt) {
    if (!opt) return
    if (multiple) {
      const next = new Set(sel)
      if (next.has(opt.value)) next.delete(opt.value); else next.add(opt.value)
      onChange?.(next)
      setQ('')
    } else {
      onChange?.(opt.value)
      setOpen(false)
    }
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) { setOpen(true); return }
      if (!filtered.length) return
      setHi((i) => {
        const n = filtered.length
        return e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n
      })
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (!open) { setOpen(true); return }
      commit(filtered[hi])
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      return
    }
    if (e.key === 'Backspace' && multiple && !q && sel.size) {
      const next = new Set(sel)
      next.delete([...next][next.size - 1])
      onChange?.(next)
    }
  }

  const picked = multiple ? null : options.find((o) => o.value === value)
  const label = multiple
    ? (sel.size ? `เลือกแล้ว ${sel.size}` : placeholder)
    : (picked?.selectedLabel || picked?.label || placeholder)
  const isPlaceholder = multiple ? !sel.size : !value

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        // The trigger truncates, and some option labels are long enough that the visible
        // part is not enough to tell two of them apart.
        title={isPlaceholder ? undefined : label}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className={`w-full flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-[13.5px] outline-none transition disabled:bg-slate-50 disabled:text-slate-400 ${
          open ? 'border-brand-400 ring-4 ring-brand-500/10' : 'border-slate-200 hover:border-slate-300'
        } ${isPlaceholder ? 'text-slate-400' : 'text-slate-700'} ${buttonClassName}`}
      >
        <span className="flex-1 text-left truncate">{label}</span>
        {multiple && sel.size > 0 && (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onChange?.(new Set()) }}
            className="text-slate-300 hover:text-slate-500"
            title="ล้าง"
          >
            <X size={14} />
          </span>
        )}
        <ChevronDown size={15} className={`text-slate-400 shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full min-w-[240px] rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          <div className="relative border-b border-slate-100">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="w-full pl-8 pr-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none"
            />
          </div>
          <div ref={listRef} className="max-h-[260px] overflow-y-auto py-1">
            {!filtered.length && <div className="px-3 py-3 text-[13px] text-slate-400">{emptyText}</div>}
            {filtered.map((o, i) => {
              const on = multiple ? sel.has(o.value) : o.value === value
              // A header wherever the group changes. It is not an option — it carries no
              // data-idx, so ↑/↓ step straight past it and `hi` stays an index into
              // `filtered`. Sticky, so the bucket you are inside stays named while you
              // scroll its block.
              const head = o.group && o.group !== filtered[i - 1]?.group ? o.group : null
              return (
                <Fragment key={o.value}>
                {head && (
                  <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    {head}
                  </div>
                )}
                <button
                  type="button"
                  data-idx={i}
                  onMouseEnter={() => setHi(i)}
                  onClick={() => commit(o)}
                  // scroll-mt keeps a sticky group header from parking on top of the row
                  // that ↑/↓ just landed on — scroll-margin only bites on scrollIntoView,
                  // so it costs nothing in a list with no headers.
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[13.5px] transition scroll-mt-8 ${
                    i === hi ? 'bg-brand-50' : ''
                  } ${on ? 'text-brand-700 font-medium' : 'text-slate-700'}`}
                >
                  <span className={`w-4 shrink-0 ${on ? 'text-brand-600' : 'text-transparent'}`}><Check size={14} /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{o.label}</span>
                    {/* `hint` is what disambiguates two people with the same ชื่อเล่น —
                        position and branch. It was already being matched by the search box
                        but never shown, so the list looked identical for both of them. */}
                    {o.hint && <span className="block truncate text-[12px] font-normal text-slate-400">{o.hint}</span>}
                  </span>
                  {o.count != null && <span className="text-[12px] text-slate-400 tabular-nums shrink-0">{o.count}</span>}
                </button>
                </Fragment>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
